import { TFunction } from "react-i18next";
import { DeliveryEmployeeApi } from "../../../../../../../../../services/Api/Organization/types";
import { capitalizeFirstLetter } from "../../../../../../../../../utils/reports";
import {
	DeliveryEmployeeFilter,
	DeliveryEmployeeFilterId,
	LiveOrdersFilters,
	externalDeliveryEmployeesArray,
} from "../../../services/types";

export const inputCustomStyles = {
	control: (provided: Record<string, any>): Record<string, any> => ({
		...provided,
		minHeight: "30px",
		height: "30px",
	}),

	valueContainer: (provided: Record<string, any>): Record<string, any> => ({
		...provided,
		height: "30px",
		padding: "0 6px",
	}),

	input: (provided: Record<string, any>): Record<string, any> => ({
		...provided,
		margin: "0px",
	}),
	indicatorsContainer: (provided: Record<string, any>): Record<string, any> => ({
		...provided,
		height: "30px",
	}),
};

export const getDeliveryEmployeeDefaultSelectedOption = (innerFilters: LiveOrdersFilters) => {
	const { deliveryEmployee } = innerFilters;

	if (deliveryEmployee?.id === "with_driver") return "with_driver";
	if (deliveryEmployee?.id === "without_driver") {
		return "without_driver";
	}
	if (deliveryEmployee?.id) return "all";
	return undefined;
};

export const getDeliveryFilterOptionTranslation = (
	option: DeliveryEmployeeFilterId,
	t: TFunction,
	maleTranslation?: true
): string => {
	switch (option) {
		case "with_driver":
			return t("modules.live_order.field.with_driver.title");
		case "without_driver":
			return t("modules.live_order.field.without_driver.title");
		case "all":
		default:
			return maleTranslation ? t("common.word.all_male") : t("lib:common.word.all");
	}
};

const employeeOptionsSortingPriority: Record<string, number> = {
	STAVA: 0,
	WOLT_DRIVE: 1,
	OTHER: 2,
};

export const getDeliveryEmployeesSelectOptions = (
	t: TFunction,
	selectedOption?: DeliveryEmployeeFilterId,
	deliveryEmployees?: DeliveryEmployeeApi[]
) => {
	const predefinedDriversOptions: { id: string; label: string }[] = [];

	if (selectedOption === "all") {
		predefinedDriversOptions.push({ id: "all", label: getDeliveryFilterOptionTranslation("all", t) });
	} else if (selectedOption === "with_driver") {
		predefinedDriversOptions.push({
			id: "with_driver",
			label: getDeliveryFilterOptionTranslation("with_driver", t, true),
		});
	}

	const employeesFromResponse = deliveryEmployees
		?.map((employee) => {
			const isExternalEmployee = externalDeliveryEmployeesArray.includes(employee?.source.id);

			return {
				id: employee.source?.id ?? "",
				label:
					(isExternalEmployee
						? capitalizeFirstLetter(employee.source?.name.toLowerCase().replaceAll("_", " "))
						: employee.employee?.name) ?? "",
			};
		})
		.sort((prevEmployee, nextEmployee) => {
			const prevEmployeePriority =
				employeeOptionsSortingPriority[prevEmployee.id] ?? employeeOptionsSortingPriority.OTHER;
			const nextEmployeePriority =
				employeeOptionsSortingPriority[nextEmployee.id] ?? employeeOptionsSortingPriority.OTHER;

			return prevEmployeePriority - nextEmployeePriority;
		});

	return [...predefinedDriversOptions, ...(employeesFromResponse ?? [])];
};

export const getDeliveryEmployeeInputValueBasedOnFilterState = (
	selectedDeliveryEmployee: DeliveryEmployeeFilter | null,
	t: TFunction,
	selectedOption: DeliveryEmployeeFilterId,
	deliveryEmployees?: DeliveryEmployeeApi[]
) => {
	const { employeeId } = selectedDeliveryEmployee ?? {};

	const isPredefinedOptionSelected = employeeId === "all" || employeeId === "with_driver";
	const isExternalEmployeeSelected = externalDeliveryEmployeesArray.includes(employeeId ?? "");
	const matchedDeliveryEmployee = deliveryEmployees?.find((deliveryEmployee) => {
		if (isExternalEmployeeSelected) {
			return deliveryEmployee.source?.id === employeeId;
		}

		return deliveryEmployee.employee_id?.toString() === selectedDeliveryEmployee?.employeeId?.toString();
	});

	let selectedOptionLabel = "";

	if (!employeeId) {
		selectedOptionLabel = getDeliveryFilterOptionTranslation(selectedOption, t, true);
	} else if (isPredefinedOptionSelected) {
		selectedOptionLabel = getDeliveryFilterOptionTranslation(employeeId, t, true);
	} else if (isExternalEmployeeSelected) {
		selectedOptionLabel = capitalizeFirstLetter(matchedDeliveryEmployee?.source?.name?.toLowerCase() ?? "");
	} else {
		selectedOptionLabel = matchedDeliveryEmployee?.employee?.name ?? "";
	}

	return {
		id: selectedDeliveryEmployee?.employeeId ?? selectedOption ?? "",
		label: selectedOptionLabel,
	};
};
